import { BaseGenerator } from './BaseGenerator.js';
import { RoomDecorator } from './RoomDecorator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Interior map generator.
 * Produces building interiors with rooms, hallways, doors, and furniture.
 * Suited for houses, shops, inns, and other indoor environments.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup passes
 * - "high"     — pipeline + room-type-aware furniture via DecorationPass
 */
export class InteriorGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || InteriorGenerator.DEFAULT_TILES;
    const opts = { ...InteriorGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — recursive subdivision produces zone map
    const ground = this.createGrid(this.width, this.height, ZONE.WALL);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // Outer shell: floor fill
    this._createOuterShellZone(ground, opts);

    // Subdivide into rooms
    const rooms = this._subdivideRoomsZone(ground, opts);

    // Place doors between rooms
    this._placeDoorsZone(ground, rooms);

    // Main entrance
    this._placeEntranceZone(ground, opts);

    // Tag narrow rooms as hallways before type assignment
    for (const room of rooms) {
      if (!room.type && (room.width <= 2 || room.height <= 2)) {
        room.type = 'hallway';
      }
    }

    // Assign room types to remaining rooms
    for (const room of rooms) {
      if (!room.type) {
        room.type = this._assignRoomType(room, opts);
      }
    }

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.FLOOR, ZONE.DOOR],
      corridorTileId: ZONE.FLOOR,
      cleanup: { minPatchSize: 0, smoothIterations: 0 },
    };

    if (quality === 'high') {
      pipelineOptions.decorations = this._buildDecorationSpecs(tiles, opts);
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones: ground,
      width: this.width,
      height: this.height,
      metadata: { rooms },
    }, pipelineOptions);

    // 3. Resolve zones to tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = result.overhead.map(row => [...row]);

    // 4. Room-type-aware furniture (operates on resolved tile IDs)
    if (quality === 'high' || opts.decorate !== false) {
      const decorator = new RoomDecorator(this.rng);
      for (const room of rooms) {
        const template = this._getRoomTemplate(room.type, tiles);
        if (template) {
          decorator.decorate(finalObjects, room, template);
        }
      }
    }

    // Hallway rugs
    if (quality === 'high') {
      this._decorateHallways(finalObjects, finalGround, tiles);
    }

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        rooms: { type: 'string', value: JSON.stringify(rooms) },
      },
    });
  }

  _createOuterShellZone(ground, opts) {
    const margin = opts.wallThickness || 1;
    for (let y = margin; y < this.height - margin; y++) {
      for (let x = margin; x < this.width - margin; x++) {
        ground[y][x] = ZONE.FLOOR;
      }
    }
  }

  _subdivideRoomsZone(ground, opts) {
    const margin = opts.wallThickness || 1;
    const rooms = [];
    const minRoom = opts.minRoomSize || 4;

    const subdivide = (x, y, w, h, depth) => {
      if (depth <= 0 || (w < minRoom * 2 + 1 && h < minRoom * 2 + 1)) {
        rooms.push({ x, y, width: w, height: h });
        return;
      }

      let splitH;
      if (w < minRoom * 2 + 1) splitH = true;
      else if (h < minRoom * 2 + 1) splitH = false;
      else splitH = this.rng.chance(h > w ? 0.7 : 0.3);

      if (splitH && h >= minRoom * 2 + 1) {
        const split = this.rng.nextInt(y + minRoom, y + h - minRoom - 1);
        for (let wx = x; wx < x + w; wx++) {
          this.setCell(ground, wx, split, ZONE.WALL);
        }
        subdivide(x, y, w, split - y, depth - 1);
        subdivide(x, split + 1, w, y + h - split - 1, depth - 1);
      } else if (!splitH && w >= minRoom * 2 + 1) {
        const split = this.rng.nextInt(x + minRoom, x + w - minRoom - 1);
        for (let wy = y; wy < y + h; wy++) {
          this.setCell(ground, split, wy, ZONE.WALL);
        }
        subdivide(x, y, split - x, h, depth - 1);
        subdivide(split + 1, y, x + w - split - 1, h, depth - 1);
      } else {
        rooms.push({ x, y, width: w, height: h });
      }
    };

    subdivide(margin, margin, this.width - margin * 2, this.height - margin * 2, opts.subdivisionDepth || 3);
    return rooms;
  }

  _placeDoorsZone(ground, rooms) {
    for (let i = 0; i < rooms.length; i++) {
      for (let j = i + 1; j < rooms.length; j++) {
        const shared = this._findSharedWallZone(rooms[i], rooms[j], ground);
        if (shared.length > 0) {
          const doorCount = Math.min(shared.length > 4 ? 2 : 1, shared.length);
          this.rng.shuffle(shared);
          for (let d = 0; d < doorCount; d++) {
            const { x, y } = shared[d];
            this.setCell(ground, x, y, ZONE.DOOR);
          }
        }
      }
    }
  }

  _findSharedWallZone(roomA, roomB, ground) {
    const shared = [];

    for (let y = Math.max(roomA.y, roomB.y) + 1; y < Math.min(roomA.y + roomA.height, roomB.y + roomB.height) - 1; y++) {
      if (roomA.x + roomA.width === roomB.x - 1 || roomA.x + roomA.width + 1 === roomB.x) {
        const wx = roomA.x + roomA.width;
        if (this.getCell(ground, wx, y) === ZONE.WALL) {
          shared.push({ x: wx, y });
        }
      }
      if (roomB.x + roomB.width === roomA.x - 1 || roomB.x + roomB.width + 1 === roomA.x) {
        const wx = roomB.x + roomB.width;
        if (this.getCell(ground, wx, y) === ZONE.WALL) {
          shared.push({ x: wx, y });
        }
      }
    }

    for (let x = Math.max(roomA.x, roomB.x) + 1; x < Math.min(roomA.x + roomA.width, roomB.x + roomB.width) - 1; x++) {
      if (roomA.y + roomA.height === roomB.y - 1 || roomA.y + roomA.height + 1 === roomB.y) {
        const wy = roomA.y + roomA.height;
        if (this.getCell(ground, x, wy) === ZONE.WALL) {
          shared.push({ x, y: wy });
        }
      }
      if (roomB.y + roomB.height === roomA.y - 1 || roomB.y + roomB.height + 1 === roomA.y) {
        const wy = roomB.y + roomB.height;
        if (this.getCell(ground, x, wy) === ZONE.WALL) {
          shared.push({ x, y: wy });
        }
      }
    }

    return shared;
  }

  _placeEntranceZone(ground, opts) {
    const doorX = Math.floor(this.width / 2);
    const doorY = this.height - 1;
    this.setCell(ground, doorX, doorY, ZONE.DOOR);
    // Mat just inside the door
    if (doorY > 1) {
      this.setCell(ground, doorX, doorY - 1, ZONE.MAT);
    }
  }

  _buildTileMapping(tiles) {
    return {
      [ZONE.WALL]:  { center: tiles.wall },
      [ZONE.FLOOR]: { center: tiles.floor },
      [ZONE.DOOR]:  { center: tiles.door || tiles.floor },
      [ZONE.MAT]:   { center: tiles.mat || tiles.floor },
    };
  }

  _buildDecorationSpecs(tiles, opts) {
    const specs = [];
    // Rugs in hallways via scatter
    if (tiles.furniture?.rug) {
      specs.push({
        tileId: ZONE.RUG,
        density: 0.06,
        spacing: 4,
        validGround: [ZONE.FLOOR],
        avoidEdges: true,
      });
    }
    return specs;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.WALL]:  tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.DOOR]:  tiles.door || tiles.floor,
      [ZONE.MAT]:   tiles.mat || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const furn = tiles.furniture || {};
    return grid.map(row => row.map(z => {
      if (z === ZONE.EMPTY || z === 0) return 0;
      if (z === ZONE.RUG) return furn.rug || 0;
      if (z === ZONE.BED) return furn.bed || 0;
      if (z === ZONE.TABLE) return furn.table || 0;
      if (z === ZONE.CHAIR) return furn.chair || 0;
      if (z === ZONE.BARREL) return furn.barrel || 0;
      if (z === ZONE.CRATE) return furn.crate || 0;
      if (z === ZONE.COUNTER) return furn.counter || 0;
      if (z === ZONE.BOOKSHELF) return furn.bookshelf || 0;
      if (z === ZONE.DESK) return furn.desk || 0;
      return z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || InteriorGenerator.DEFAULT_TILES;
    const opts = { ...InteriorGenerator.DEFAULT_OPTIONS, ...this.options };

    const ground = this.createGrid(this.width, this.height, tiles.wall);
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    // 1. Outer walls and main floor
    this._createOuterShell(ground, tiles, opts);

    // 2. Subdivide into rooms
    const rooms = this._subdivideRooms(ground, tiles, opts);

    // 3. Place doors between rooms
    this._placeDoors(ground, rooms, tiles);

    // 4. Main entrance
    this._placeEntrance(ground, tiles, opts);

    // 5. Tag narrow rooms as hallways, then assign remaining room types
    for (const room of rooms) {
      if (!room.type && (room.width <= 2 || room.height <= 2)) {
        room.type = 'hallway';
      }
    }

    const decorator = new RoomDecorator(this.rng);
    for (const room of rooms) {
      const roomType = room.type || this._assignRoomType(room, opts);
      room.type = roomType;
      const template = this._getRoomTemplate(roomType, tiles);
      if (template) {
        decorator.decorate(objects, room, template);
      }
    }

    // 6. Hallway decoration
    this._decorateHallways(objects, ground, tiles);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground, objects, overhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(ground) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        rooms: { type: 'string', value: JSON.stringify(rooms) },
      },
    });
  }

  // ---- Original helpers (used by draft mode) ----

  _createOuterShell(ground, tiles, opts) {
    const margin = opts.wallThickness || 1;
    for (let y = margin; y < this.height - margin; y++) {
      for (let x = margin; x < this.width - margin; x++) {
        ground[y][x] = tiles.floor;
      }
    }
  }

  _subdivideRooms(ground, tiles, opts) {
    const margin = opts.wallThickness || 1;
    const rooms = [];
    const minRoom = opts.minRoomSize || 4;

    const subdivide = (x, y, w, h, depth) => {
      if (depth <= 0 || (w < minRoom * 2 + 1 && h < minRoom * 2 + 1)) {
        rooms.push({ x, y, width: w, height: h });
        return;
      }

      let splitH;
      if (w < minRoom * 2 + 1) splitH = true;
      else if (h < minRoom * 2 + 1) splitH = false;
      else splitH = this.rng.chance(h > w ? 0.7 : 0.3);

      if (splitH && h >= minRoom * 2 + 1) {
        const split = this.rng.nextInt(y + minRoom, y + h - minRoom - 1);
        for (let wx = x; wx < x + w; wx++) {
          this.setCell(ground, wx, split, tiles.wall);
        }
        subdivide(x, y, w, split - y, depth - 1);
        subdivide(x, split + 1, w, y + h - split - 1, depth - 1);
      } else if (!splitH && w >= minRoom * 2 + 1) {
        const split = this.rng.nextInt(x + minRoom, x + w - minRoom - 1);
        for (let wy = y; wy < y + h; wy++) {
          this.setCell(ground, split, wy, tiles.wall);
        }
        subdivide(x, y, split - x, h, depth - 1);
        subdivide(split + 1, y, x + w - split - 1, h, depth - 1);
      } else {
        rooms.push({ x, y, width: w, height: h });
      }
    };

    subdivide(margin, margin, this.width - margin * 2, this.height - margin * 2, opts.subdivisionDepth || 3);
    return rooms;
  }

  _placeDoors(ground, rooms, tiles) {
    for (let i = 0; i < rooms.length; i++) {
      for (let j = i + 1; j < rooms.length; j++) {
        const shared = this._findSharedWall(rooms[i], rooms[j], ground, tiles);
        if (shared.length > 0) {
          const doorCount = Math.min(shared.length > 4 ? 2 : 1, shared.length);
          this.rng.shuffle(shared);
          for (let d = 0; d < doorCount; d++) {
            const { x, y } = shared[d];
            this.setCell(ground, x, y, tiles.door || tiles.floor);
          }
        }
      }
    }
  }

  _findSharedWall(roomA, roomB, ground, tiles) {
    const shared = [];

    for (let y = Math.max(roomA.y, roomB.y) + 1; y < Math.min(roomA.y + roomA.height, roomB.y + roomB.height) - 1; y++) {
      if (roomA.x + roomA.width === roomB.x - 1 || roomA.x + roomA.width + 1 === roomB.x) {
        const wx = roomA.x + roomA.width;
        if (this.getCell(ground, wx, y) === tiles.wall) {
          shared.push({ x: wx, y });
        }
      }
      if (roomB.x + roomB.width === roomA.x - 1 || roomB.x + roomB.width + 1 === roomA.x) {
        const wx = roomB.x + roomB.width;
        if (this.getCell(ground, wx, y) === tiles.wall) {
          shared.push({ x: wx, y });
        }
      }
    }

    for (let x = Math.max(roomA.x, roomB.x) + 1; x < Math.min(roomA.x + roomA.width, roomB.x + roomB.width) - 1; x++) {
      if (roomA.y + roomA.height === roomB.y - 1 || roomA.y + roomA.height + 1 === roomB.y) {
        const wy = roomA.y + roomA.height;
        if (this.getCell(ground, x, wy) === tiles.wall) {
          shared.push({ x, y: wy });
        }
      }
      if (roomB.y + roomB.height === roomA.y - 1 || roomB.y + roomB.height + 1 === roomA.y) {
        const wy = roomB.y + roomB.height;
        if (this.getCell(ground, x, wy) === tiles.wall) {
          shared.push({ x, y: wy });
        }
      }
    }

    return shared;
  }

  _placeEntrance(ground, tiles, opts) {
    const doorX = Math.floor(this.width / 2);
    const doorY = this.height - 1;
    this.setCell(ground, doorX, doorY, tiles.door || tiles.floor);
    if (tiles.mat) {
      this.setCell(ground, doorX, doorY - 1, tiles.mat);
    }
  }

  _assignRoomType(room, opts) {
    const types = opts.roomTypes || ['bedroom', 'kitchen', 'storage', 'living', 'study'];
    return this.rng.pick(types);
  }

  _getRoomTemplate(type, tiles) {
    const templates = {
      bedroom: {
        features: [
          { type: 'corners', tileIds: [tiles.furniture?.bed || 0].filter(Boolean), inset: 1 },
          { type: 'scatter', tileIds: [tiles.furniture?.chest || 0].filter(Boolean), count: 2, margin: 1 },
          { type: 'scatter', tileIds: [tiles.furniture?.rug || 0].filter(Boolean), count: 1, margin: 1 },
        ],
      },
      kitchen: {
        features: [
          { type: 'wall', tileId: tiles.furniture?.counter || 0, side: 'north', spacing: 1, margin: 1 },
          { type: 'scatter', tileIds: [tiles.furniture?.barrel || 0].filter(Boolean), count: 3, margin: 1 },
          { type: 'scatter', tileIds: [tiles.furniture?.crate || 0].filter(Boolean), count: 1, margin: 1 },
        ],
      },
      storage: {
        features: [
          { type: 'scatter', tileIds: [tiles.furniture?.barrel || 0, tiles.furniture?.crate || 0].filter(Boolean), count: 6, margin: 1 },
        ],
      },
      living: {
        features: [
          { type: 'center', tileId: tiles.furniture?.table || 0 },
          { type: 'scatter', tileIds: [tiles.furniture?.chair || 0].filter(Boolean), count: 4, margin: 1 },
          { type: 'scatter', tileIds: [tiles.furniture?.rug || 0].filter(Boolean), count: 1, margin: 1 },
        ],
      },
      study: {
        features: [
          { type: 'wall', tileId: tiles.furniture?.bookshelf || 0, side: 'north', spacing: 1, margin: 1 },
          { type: 'center', tileId: tiles.furniture?.desk || 0 },
          { type: 'scatter', tileIds: [tiles.furniture?.chair || 0].filter(Boolean), count: 2, margin: 1 },
        ],
      },
      hallway: {
        features: [
          { type: 'scatter', tileIds: [tiles.furniture?.rug || 0].filter(Boolean), count: 2, margin: 1 },
        ],
      },
    };

    return templates[type] || null;
  }

  _decorateHallways(objects, ground, tiles) {
    if (!tiles.furniture?.rug) return;

    for (let y = 2; y < this.height - 2; y++) {
      for (let x = 2; x < this.width - 2; x++) {
        if (ground[y][x] !== tiles.floor || objects[y][x] !== 0) continue;
        if (this.rng.chance(0.08)) {
          this.setCell(objects, x, y, tiles.furniture.rug);
        }
      }
    }
  }

  static DEFAULT_TILES = {
    wall: 1,
    floor: 3,
    door: 4,
    mat: 5,
    furniture: {
      bed: 10,
      chest: 11,
      table: 12,
      chair: 13,
      barrel: 14,
      crate: 15,
      counter: 16,
      bookshelf: 17,
      desk: 18,
      rug: 19,
    },
  };

  static DEFAULT_OPTIONS = {
    wallThickness: 1,
    minRoomSize: 4,
    subdivisionDepth: 3,
    roomTypes: ['bedroom', 'kitchen', 'storage', 'living', 'study'],
  };

  static getEmittedZones() {
    return [
      ZONE.WALL, ZONE.FLOOR, ZONE.DOOR, ZONE.BED, ZONE.TABLE, ZONE.CHAIR, ZONE.BARREL, ZONE.CRATE, ZONE.COUNTER, ZONE.BOOKSHELF, ZONE.DESK, ZONE.RUG, ZONE.MAT,
    ];
  }
}
